import { useState } from 'react'
import { format } from 'date-fns'
import { th } from 'date-fns/locale'
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/common/ui/table'
import { Card, CardContent, CardHeader, CardTitle } from '@/common/ui/card'
import { Badge } from '@/common/ui/badge'
import { Button } from '@/common/ui/button'
import {
	Copy,
	CheckCircle2,
	ArrowUpDown,
	ChevronDown,
	ChevronUp,
} from 'lucide-react'
import { toast } from 'sonner'
import { CampaignAnalyticsLineTransactionType } from '@/schema'
import {
	formatMessageType,
	getMessageTypeBadgeColor,
	getBestPerformingPeriod,
	formatConversionRate,
	formatNumber,
} from '../../utils/formatConversionData'
import CategoryConversionTable from '../CategoryConversionTable'

import type { CampaignAnalytics } from '../../types'

// Format LINE transaction type for display
function formatLineTransactionType(
	type: CampaignAnalyticsLineTransactionType | undefined,
): string {
	if (!type) return ''
	switch (type) {
		case CampaignAnalyticsLineTransactionType.TEXT:
			return 'Text'
		case CampaignAnalyticsLineTransactionType.FLEX_HOT_GAME:
			return 'Flex'
		default:
			return type
	}
}

// Get badge color for LINE transaction type
function getLineTransactionTypeBadgeColor(
	type: CampaignAnalyticsLineTransactionType | undefined,
): string {
	switch (type) {
		case CampaignAnalyticsLineTransactionType.TEXT:
			return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-300'
		case CampaignAnalyticsLineTransactionType.FLEX_HOT_GAME:
			return 'bg-pink-100 text-pink-800 dark:bg-pink-900 dark:text-pink-300'
		default:
			return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-300'
	}
}

interface CampaignAnalyticsTableProps {
	campaigns: CampaignAnalytics[]
	isLoading?: boolean
	title?: string
}

type SortField =
	| 'recipients'
	| 'depositRate'
	| 'sentAt'
	| 'reactivationCount'
	| null
type SortOrder = 'asc' | 'desc'

export default function CampaignAnalyticsTable({
	campaigns,
	isLoading,
	title = 'รายละเอียดแคมเปญ',
}: CampaignAnalyticsTableProps) {
	const [copiedId, setCopiedId] = useState<string | null>(null)
	const [sortField, setSortField] = useState<SortField>('sentAt')
	const [sortOrder, setSortOrder] = useState<SortOrder>('desc')
	const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set())

	const copyToClipboard = async (text: string) => {
		try {
			await navigator.clipboard.writeText(text)
			setCopiedId(text)
			toast.success('คัดลอกแล้ว')
			setTimeout(() => setCopiedId(null), 2000)
		} catch {
			toast.error('ไม่สามารถคัดลอกได้')
		}
	}

	const handleSort = (field: SortField) => {
		if (sortField === field) {
			setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
		} else {
			setSortField(field)
			setSortOrder('desc')
		}
	}

	const toggleRowExpand = (transactionId: string) => {
		const newExpanded = new Set(expandedRows)
		if (newExpanded.has(transactionId)) {
			newExpanded.delete(transactionId)
		} else {
			newExpanded.add(transactionId)
		}
		setExpandedRows(newExpanded)
	}

	// Get total conversion count
	const getTotalConversionCount = (campaign: CampaignAnalytics) => {
		const categories = campaign.depositConversionByCategory
		if (!categories) return 0
		return (
			categories.newDepositorCount +
			categories.inactive7dCount +
			categories.inactive14dCount +
			categories.inactive30dCount +
			categories.regularUserCount
		)
	}

	// Sort campaigns
	const sortedCampaigns = [...campaigns].sort((a, b) => {
		if (!sortField) return 0

		let aValue: number
		let bValue: number

		switch (sortField) {
			case 'recipients':
				aValue = a.totalRecipients
				bValue = b.totalRecipients
				break
			case 'depositRate':
				aValue = getBestPerformingPeriod(a.depositConversion).rate
				bValue = getBestPerformingPeriod(b.depositConversion).rate
				break
			case 'sentAt':
				aValue = new Date(a.sentAt).getTime()
				bValue = new Date(b.sentAt).getTime()
				break
			case 'reactivationCount':
				aValue = getTotalConversionCount(a)
				bValue = getTotalConversionCount(b)
				break
			default:
				return 0
		}

		return sortOrder === 'asc' ? aValue - bValue : bValue - aValue
	})

	if (isLoading) {
		return (
			<Card>
				<CardHeader>
					<CardTitle>{title}</CardTitle>
				</CardHeader>
				<CardContent>
					<div className="h-64 animate-pulse rounded bg-muted" />
				</CardContent>
			</Card>
		)
	}

	if (campaigns.length === 0) {
		return (
			<Card>
				<CardHeader>
					<CardTitle>{title}</CardTitle>
				</CardHeader>
				<CardContent>
					<div className="flex flex-col items-center justify-center py-12 text-center">
						<p className="text-muted-foreground">
							ไม่พบข้อมูลแคมเปญในช่วงเวลาที่เลือก
						</p>
					</div>
				</CardContent>
			</Card>
		)
	}

	return (
		<Card>
			<CardHeader>
				<CardTitle>
					{title} ({campaigns.length})
				</CardTitle>
				<p className="text-sm text-muted-foreground">
					คลิกแถวเพื่อดูรายละเอียด Conversion ตามกลุ่ม
				</p>
			</CardHeader>
			<CardContent>
				<div className="overflow-x-auto">
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead className="w-[40px]"></TableHead>
								<TableHead className="w-[150px]">Transaction ID</TableHead>
								<TableHead className="max-w-[250px]">ข้อความ</TableHead>
								<TableHead>ประเภท</TableHead>
								<TableHead>
									<Button
										variant="ghost"
										size="sm"
										onClick={() => handleSort('sentAt')}
										className="h-8"
									>
										วันที่ส่ง
										<ArrowUpDown className="ml-2 h-4 w-4" />
									</Button>
								</TableHead>
								<TableHead className="text-right">
									<Button
										variant="ghost"
										size="sm"
										onClick={() => handleSort('recipients')}
										className="h-8"
									>
										ผู้รับ
										<ArrowUpDown className="ml-2 h-4 w-4" />
									</Button>
								</TableHead>
								<TableHead className="text-right">
									<Button
										variant="ghost"
										size="sm"
										onClick={() => handleSort('reactivationCount')}
										className="h-8"
									>
										Reactivation
										<ArrowUpDown className="ml-2 h-4 w-4" />
									</Button>
								</TableHead>
								<TableHead className="text-right">
									<Button
										variant="ghost"
										size="sm"
										onClick={() => handleSort('depositRate')}
										className="h-8"
									>
										อัตราฝาก (สูงสุด)
										<ArrowUpDown className="ml-2 h-4 w-4" />
									</Button>
								</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{sortedCampaigns.map((campaign) => {
								const bestDepositRate = getBestPerformingPeriod(
									campaign.depositConversion,
								)
								const isExpanded = expandedRows.has(campaign.transactionId)
								const totalConversionCount = getTotalConversionCount(campaign)

								return (
									<>
										<TableRow
											key={campaign.transactionId}
											className="cursor-pointer hover:bg-muted/50"
											onClick={() => toggleRowExpand(campaign.transactionId)}
										>
											<TableCell>
												{isExpanded ? (
													<ChevronUp className="h-4 w-4" />
												) : (
													<ChevronDown className="h-4 w-4" />
												)}
											</TableCell>
											<TableCell>
												<div className="flex items-center gap-2">
													<code className="text-xs">
														{campaign.transactionId.slice(0, 8)}...
													</code>
													<Button
														variant="ghost"
														size="sm"
														onClick={(e) => {
															e.stopPropagation()
															copyToClipboard(campaign.transactionId)
														}}
													>
														{copiedId === campaign.transactionId ? (
															<CheckCircle2 className="h-3 w-3 text-green-600" />
														) : (
															<Copy className="h-3 w-3" />
														)}
													</Button>
												</div>
											</TableCell>
											<TableCell>
												<div
													className="max-w-[250px] truncate"
													title={campaign.message || '-'}
												>
													{campaign.message || '-'}
												</div>
											</TableCell>
											<TableCell>
												<div className="flex flex-wrap items-center gap-1">
													<Badge
														className={getMessageTypeBadgeColor(
															campaign.messageType as
																| 'IMMEDIATE'
																| 'SCHEDULED'
																| 'AUTO'
																| 'INTERVAL',
														)}
													>
														{formatMessageType(
															campaign.messageType as
																| 'IMMEDIATE'
																| 'SCHEDULED'
																| 'AUTO'
																| 'INTERVAL',
														)}
													</Badge>
													{campaign.channel === 'SMS' ? (
														<Badge className="bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-300">
															SMS
														</Badge>
													) : (
														<Badge
															className={getLineTransactionTypeBadgeColor(
																campaign.lineTransactionType,
															)}
														>
															LINE
															{campaign.lineTransactionType
																? ` ${formatLineTransactionType(campaign.lineTransactionType)}`
																: ''}
														</Badge>
													)}
												</div>
											</TableCell>
											<TableCell className="text-sm">
												{format(
													new Date(campaign.sentAt),
													'dd MMM yyyy HH:mm',
													{
														locale: th,
													},
												)}
											</TableCell>
											<TableCell className="text-right font-semibold">
												{formatNumber(campaign.successfulDeliveries)}
											</TableCell>
											<TableCell className="text-right">
												<span className="font-semibold text-violet-600 dark:text-violet-400">
													{formatNumber(totalConversionCount)}
												</span>
											</TableCell>
											<TableCell className="text-right">
												<div className="flex flex-col items-end">
													<span className="font-semibold text-green-600 dark:text-green-400">
														{formatConversionRate(bestDepositRate.rate)}
													</span>
													<span className="text-xs text-muted-foreground">
														{bestDepositRate.period}
													</span>
												</div>
											</TableCell>
										</TableRow>

										{/* Expanded Category Details */}
										{isExpanded && campaign.depositConversionByCategory && (
											<TableRow key={`${campaign.transactionId}-expanded`}>
												<TableCell colSpan={8} className="bg-muted/20 p-0">
													<CategoryConversionTable
														metrics={campaign.depositConversionByCategory}
														embedded
													/>
												</TableCell>
											</TableRow>
										)}
									</>
								)
							})}
						</TableBody>
					</Table>
				</div>
			</CardContent>
		</Card>
	)
}
